import * as React from 'react';
import qs from 'qs';
import { Redirect, Route, Switch, withRouter, } from 'react-router-dom';

import Sidebar, { ASSIGN_GROWTH_DIAGNOSTIC, ASSIGN_GROWTH_DIAGNOSTIC_SUB_PAGE, REPORT_PAGES, } from './sidebar'
import AssignGrowthDiagnostic from './assignGrowthDiagnostic';
import GrowthResults from './growthResults';
import GrowthSummary from './growthSummary';
import IndividualStudentResponses from './individualStudentResponses';
import NotYetAssigned from './notYetAssigned';
import Questions from './questions';
import Recommendations from './recommendations';
import Results from './results';
import StudentResponsesIndex from './studentResponsesIndex';
import Summary from './summary';

import { requestGet, } from '../../../../../../modules/request/index';
import { DropdownInput, SMALL_ICON_BASE_SRC, diagnosticSequences, getPageTitle, } from '../../../../../Shared/index';
import { RESULTS_AND_RECOMMENDATIONS_FEATURED_BLOG_POST_ID } from '../../../../constants/featuredBlogPost';
import ArticleSpotlight from '../../../shared/articleSpotlight';
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from '../../progress_report_constants';

const openSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-open.svg`
const closeSidebarIconSrc = `${SMALL_ICON_BASE_SRC}/sidebar-close.svg`

const GROWTH_TO_BASELINE_SUB_PAGE = REPORT_PAGES.reduce((subPages, page) => (
  page.postSubPage === page.preSubPage ? subPages : { ...subPages, [page.postSubPage]: page.preSubPage }
), {})

const SUB_PAGE_TITLES = REPORT_PAGES.reduce((titles, page) => (
  { ...titles, [page.preSubPage]: page.label, [page.postSubPage]: page.label }
), { [ASSIGN_GROWTH_DIAGNOSTIC_SUB_PAGE]: ASSIGN_GROWTH_DIAGNOSTIC })

const CANONICAL_DIAGNOSTIC_OPTIONS = diagnosticSequences.map(s => ({ value: s.preTest.activityId, label: s.label, }))
const CANONICAL_PRE_TEST_ACTIVITY_IDS = new Set(CANONICAL_DIAGNOSTIC_OPTIONS.map(option => option.value))

const mobileLinkOptions = (diagnostic, search) => {
  if (!diagnostic) { return [] }

  const baseLinkPathPre = `/diagnostics/${diagnostic.pre.activity_id}/classroom/${diagnostic.pre.classroom_id}`
  const baseLinkPathPost = diagnostic.post && `/diagnostics/${diagnostic.post.activity_id}/classroom/${diagnostic.post.classroom_id}`
  const queryString = search ? search : ''

  function pathsArray(isPostDiagnostic) {
    const prefix = isPostDiagnostic ? 'Growth' : 'Baseline'
    const baseLink = isPostDiagnostic ? baseLinkPathPost : baseLinkPathPre
    return REPORT_PAGES.map(page => ({
      label: `${prefix} - ${page.label}`,
      value: `${baseLink}/${isPostDiagnostic ? page.postSubPage : page.preSubPage}${queryString}`
    }))
  }

  const baselineOptions = pathsArray(false)

  if (diagnostic.post) {
    baselineOptions.push({ label: ASSIGN_GROWTH_DIAGNOSTIC, value: `${baseLinkPathPre}/${ASSIGN_GROWTH_DIAGNOSTIC_SUB_PAGE}${queryString}` })
  }

  return diagnostic.post && diagnostic.post.assigned_count ? baselineOptions.concat(pathsArray(true)) : baselineOptions
}

const IndividualPack = ({ classrooms, history, match, location, lessonsBannerIsShowable, }) => {
  const [showSidebar, setShowSidebar] = React.useState(true)
  const [readyStudentCount, setReadyStudentCount] = React.useState(null)

  const unitId = qs.parse(location.search.replace('?', '')).unit
  const activityId = Number(match.params.activityId)
  const classroomId = Number(match.params.classroomId)
  const splitPathName = location.pathname.split('/')
  const subPage = splitPathName.includes('responses') ? 'responses' : splitPathName[splitPathName.length - 1]
  const activeClassroom = classrooms.find(c =>  c.id === classroomId )
  const activeDiagnostic = activeClassroom && diagnosticForClassroom(activeClassroom)
  const canonicalSequence = diagnosticSequences.find(s => s.preTest.activityId === activityId || s.postTest?.activityId === activityId)
  const baselinePreActivityId = activeDiagnostic?.pre?.activity_id
  const sequenceHasGrowth = !!activeDiagnostic?.pre?.post_test_id

  React.useEffect(() => {
    setReadyStudentCount(null)
    getReadyStudentCount()
  }, [baselinePreActivityId, classroomId, sequenceHasGrowth, unitId])

  React.useEffect(() => {
    if (!activeClassroom) { return }

    const titleParts = activeDiagnostic
      ? [SUB_PAGE_TITLES[subPage], diagnosticActivityName(), activeClassroom.name]
      : [canonicalSequence?.label, activeClassroom.name]
    document.title = getPageTitle(...titleParts)
  }, [subPage, activityId, classroomId])

  function getReadyStudentCount() {
    if (!baselinePreActivityId || !sequenceHasGrowth) { return }

    const unitQueryString = unitId ? `?unit_id=${unitId}` : ''
    requestGet(`/teachers/progress_reports/growth_diagnostic_ready_student_count/${classroomId}/activity/${baselinePreActivityId}${unitQueryString}`, (data) => setReadyStudentCount(data.count))
  }

  function toggleSidebar() { setShowSidebar(!showSidebar) }

  function diagnosticForClassroom(classroom) {
    return classroom.diagnostics.find(d => {
      const assignedDiagnosticAsEitherPreOrPost = (d.pre.activity_id === activityId) || (d.post && d.post.activity_id === activityId)
      const preOrPostHasUnitId = d.pre.unit_id || d.post?.unit_id
      if (assignedDiagnosticAsEitherPreOrPost && unitId && preOrPostHasUnitId) {
        return d.pre.unit_id === Number(unitId) || d.post?.unit_id === Number(unitId)
      }
      return assignedDiagnosticAsEitherPreOrPost
    })
  }

  function diagnosticActivityName() {
    if (!activeDiagnostic) { return '' }

    if (activeDiagnostic.pre.activity_id === activityId) {
      return activeDiagnostic.pre.activity_name
    }

    if (activeDiagnostic.post && activeDiagnostic.post.activity_id === activityId) {
      return activeDiagnostic.post.activity_name
    }

    return ''
  }

  function activeDiagnosticIsPost() {
    if (!activeDiagnostic) { return false }

    if (activeDiagnostic.post && activeDiagnostic.post.activity_id === activityId) {
      return true
    }

    return false
  }

  function eligibleForQuestionScoring() {
    if (!activeDiagnostic) { return false }

    // we only care about whether or not the pre-diagnostic is eligible because even if the post- is, we can't compare the data and so need to keep the empty state
    return activeDiagnostic.pre.eligible_for_question_scoring
  }

  function onClassesDropdownChange(e) {
    const newClassroom = classrooms.find(c => c.id === e.value)
    const parallelDiagnostic = diagnosticForClassroom(newClassroom)
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, e.value)

    if (parallelDiagnostic) {
      // the following line handles the case where we are currently viewing a post-diagnostic page, but the parallel classroom hasn't yet been assigned the post diagnostic
      const newActivityId = parallelDiagnostic.post && parallelDiagnostic.post.activity_id === activityId ? activityId : parallelDiagnostic.pre.activity_id
      history.push(`/diagnostics/${newActivityId}/classroom/${e.value}/${subPage}`)
      return
    }

    const sequencePreId = activeDiagnostic ? activeDiagnostic.pre.activity_id : activityId
    history.push(`/diagnostics/${sequencePreId}/classroom/${e.value}/${GROWTH_TO_BASELINE_SUB_PAGE[subPage] || subPage}`)
  }

  function onDiagnosticsDropdownChange(e) {
    const targetDiagnostic = activeClassroom.diagnostics.find(d => d.pre.activity_id === e.value)

    if (!targetDiagnostic) {
      history.push(`/diagnostics/${e.value}/classroom/${classroomId}/summary`)
      return
    }

    const usePostId = activeDiagnosticIsPost() && targetDiagnostic.post?.assigned_count
    const newActivityId = usePostId ? targetDiagnostic.post.activity_id : targetDiagnostic.pre.activity_id
    const newSubPage = usePostId ? subPage : (GROWTH_TO_BASELINE_SUB_PAGE[subPage] || subPage)
    history.push(`/diagnostics/${newActivityId}/classroom/${classroomId}/${newSubPage}`)
  }

  function onLinkDropdownChange(e) {
    history.push(e.value)
  }

  function renderShowSidebarButton() {
    const ariaLabel = showSidebar ? 'Close sidebar' : 'Open sidebar'

    const imgSrc = showSidebar ? closeSidebarIconSrc : openSidebarIconSrc

    return (
      <button
        aria-expanded={showSidebar}
        aria-label={ariaLabel}
        className='interactive-wrapper focus-on-light show-sidebar-button'
        onClick={toggleSidebar}
        type="button"
      >
        <img alt="" src={imgSrc} />
      </button>
    )
  }

  if (!activeClassroom || (!activeDiagnostic && !canonicalSequence)) {
    return <Redirect to="/diagnostics" />
  }

  const classroomDropdownOptions = classrooms.map(c => ({ value: c.id, label: c.name, }))
  const selectedClassroom = classroomDropdownOptions.find(opt => String(opt.value) === match.params.classroomId)

  const assignedNonCanonicalOptions = activeClassroom.diagnostics
    .filter(d => !CANONICAL_PRE_TEST_ACTIVITY_IDS.has(d.pre.activity_id))
    .map(d => ({ value: d.pre.activity_id, label: d.name, }))
  const diagnosticDropdownOptions = CANONICAL_DIAGNOSTIC_OPTIONS.concat(assignedNonCanonicalOptions)

  const selectedSequencePreTestActivityId = activeDiagnostic ? activeDiagnostic.pre.activity_id : canonicalSequence.preTest.activityId
  const selectedDiagnostic = diagnosticDropdownOptions.find(opt => opt.value === selectedSequencePreTestActivityId)

  const hasGrowth = activeDiagnostic ? !!activeDiagnostic.pre.post_test_id : !!canonicalSequence.postTest

  const classroomDropdown = (<DropdownInput
    ariaLabel="Classroom"
    className="focus-on-light"
    handleChange={onClassesDropdownChange}
    id="mobile-classroom-dropdown"
    isSearchable={false}
    options={classroomDropdownOptions}
    value={selectedClassroom}
  />)

  const diagnosticDropdown = (<DropdownInput
    ariaLabel="Diagnostic"
    className="focus-on-light"
    handleChange={onDiagnosticsDropdownChange}
    id="mobile-diagnostic-dropdown"
    isSearchable={false}
    options={diagnosticDropdownOptions}
    value={selectedDiagnostic}
  />)

  const linkDropdownOptions = mobileLinkOptions(activeDiagnostic, location.search)

  const linkDropdown = (<DropdownInput
    ariaLabel="Link"
    handleChange={onLinkDropdownChange}
    id="link-dropdown"
    isSearchable={false}
    options={linkDropdownOptions}
    value={linkDropdownOptions.find(opt => window.location.href.includes(opt.value))}
  />)

  const mobileNavigation = (<section className="mobile-navigation">{classroomDropdown}{diagnosticDropdown}{activeDiagnostic ? linkDropdown : null}</section>)

  const sharedProps = {
    activityName: diagnosticActivityName(),
    classrooms,
    mobileNavigation,
    lessonsBannerIsShowable,
    isPostDiagnostic: activeDiagnosticIsPost(),
    postDiagnosticUnitTemplateId: activeDiagnostic?.post?.unit_template_id,
    postDiagnosticActivityId: activeDiagnostic?.pre?.post_test_id,
    refreshReadyStudentCount: getReadyStudentCount,
    eligibleForQuestionScoring: eligibleForQuestionScoring()
  }

  function renderAssignGrowthDiagnostic() {
    if (!activeDiagnostic?.pre?.post_test_id) { return <Redirect to={`/diagnostics/${activityId}/classroom/${classroomId}/summary`} /> }

    return <AssignGrowthDiagnostic {...sharedProps} />
  }
  function renderGrowthSummary() { return <GrowthSummary {...sharedProps} /> }
  function renderGrowthResults() { return <GrowthResults {...sharedProps} /> }
  function renderSummary() { return <Summary {...sharedProps} /> }
  function renderResults() { return <Results {...sharedProps} /> }
  function renderRecommendations() { return <Recommendations {...sharedProps} /> }
  function renderQuestions() { return <Questions {...sharedProps} /> }
  function renderIndividualStudentResponses() { return <IndividualStudentResponses {...sharedProps} /> }
  function renderStudentResponsesIndex() { return <StudentResponsesIndex {...sharedProps} /> }

  return (
    <React.Fragment>
      <div className="white-background-accommodate-footer">
        <div className="diagnostic-individual-pack">
          {showSidebar && (
            <Sidebar
              activeDiagnostic={activeDiagnostic}
              assignGrowthDiagnosticBadgeCount={readyStudentCount}
              classroomOptions={classroomDropdownOptions}
              diagnosticOptions={diagnosticDropdownOptions}
              hasGrowth={hasGrowth}
              location={location}
              onClassroomChange={onClassesDropdownChange}
              onDiagnosticChange={onDiagnosticsDropdownChange}
              selectedClassroom={selectedClassroom}
              selectedDiagnostic={selectedDiagnostic}
              showSidebarButton={renderShowSidebarButton()}
            />
          )}
          <div className={showSidebar ? 'sidebar-open' : 'sidebar-closed'}>
            {showSidebar ? null : renderShowSidebarButton()}
            {activeDiagnostic ? (
              <Switch>
                <Route path='/diagnostics/:activityId/classroom/:classroomId/assign_growth_diagnostic' render={renderAssignGrowthDiagnostic} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/growth_summary' render={renderGrowthSummary} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/growth_results' render={renderGrowthResults} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/summary' render={renderSummary} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/results' render={renderResults} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/recommendations' render={renderRecommendations} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/questions' render={renderQuestions} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/responses/:studentId' render={renderIndividualStudentResponses} />
                <Route path='/diagnostics/:activityId/classroom/:classroomId/responses' render={renderStudentResponsesIndex} />
              </Switch>
            ) : (
              <NotYetAssigned classroomId={classroomId} classroomName={activeClassroom.name} mobileNavigation={mobileNavigation} sequence={canonicalSequence} />
            )}
          </div>
        </div>
      </div>
      <ArticleSpotlight blogPostId={RESULTS_AND_RECOMMENDATIONS_FEATURED_BLOG_POST_ID} />
    </React.Fragment>
  )
}

export default withRouter(IndividualPack)
